/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
// Global
import { Fragment, useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import { Text } from '@sitecore-content-sdk/nextjs';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import useDictionary from 'lib/hooks/useDictionary';
import { useRealPathName } from 'lib/hooks/useRealPathName';
import useClickOutside from 'lib/hooks/useClickOutside';
import { SvgIcon } from 'helpers/SvgIcon';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { getTestProps } from 'lib/testing/utils';
import { getCanonicalUrl } from 'lib/seo/getCanonicalUrl';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const Share = ()=>{
    const isEditing = useIsEditing();
    const { getDictionaryValue } = useDictionary();
    const context = useSitecoreContext();
    const siteSettings = context?.sitecoreContext?.siteSettings;
    const socialShareLinks = siteSettings?.socialShareLinks;
    const [currentUrl, setCurrentUrl] = useState('');
    const [toggleShareMenu, setToggleShareMenu] = useState(false);
    const [isClickedOnCopyLink, setIsClickedOnCopyLink] = useState(false);
    const componentRef = useRef(null);
    const page = context?.sitecoreContext?.route;
    const Title = page?.fields?.OpenGraphTitle;
    const Description = page?.fields?.OpenGraphDescription;
    const ogImage = page?.fields?.OpenGraphImageUrl;
    const canonicalUrl = page?.fields?.canonicalUrl?.value;
    const itemPath = context?.sitecoreContext?.itemPath;
    const resolvedCanonicalUrl = getCanonicalUrl({
        explicitUrl: canonicalUrl,
        itemPath
    });
    const pathName = useRealPathName();
    useEffect(()=>{
        const isBrowser = "object" !== 'undefined';
        if (isBrowser) {
            setCurrentUrl(window.location.href);
        }
    }, [
        pathName
    ]);
    const handleOnClick = async ()=>{
        if (navigator.share) {
            const payload = {
                url: resolvedCanonicalUrl || currentUrl,
                title: Title?.value,
                text: Description?.value
            };
            const canShare = navigator.canShare(payload);
            if (!canShare) return;
            navigator.share(payload).catch(console.error);
        } else {
            setToggleShareMenu(!toggleShareMenu);
        }
    };
    // Close share dropdown when click on outside
    const handleOutsideClick = ()=>{
        setToggleShareMenu(false);
    };
    // Click outside hook.
    useClickOutside(componentRef, toggleShareMenu, handleOutsideClick);
    const handleShareClick = (shareItem = '')=>{
        switch(shareItem?.toLowerCase()){
            case 'print':
                window.print();
                break;
            case 'copy':
                setIsClickedOnCopyLink(!isClickedOnCopyLink);
                navigator.clipboard.writeText(resolvedCanonicalUrl || currentUrl).then(()=>{
                    setTimeout(()=>{
                        setIsClickedOnCopyLink(false);
                        setToggleShareMenu(false);
                    }, 3000);
                }).catch((err)=>{
                    console.error('Error copying text to clipboard', err);
                });
                break;
            default:
                break;
        }
    };
    const getRedirectionLink = (urlLink = '')=>{
        const redirectionLink = urlLink.replace('$url', resolvedCanonicalUrl || currentUrl).replaceAll('$title', encodeURIComponent(Title?.value ?? '')).replace('$description', encodeURIComponent(Description?.value ?? '')).replace('$image', ogImage?.value?.src ?? '');
        return redirectionLink;
    };
    const { base, cta, content, shareListPanel, header, headerText, closeIcon, shareLinkItem, shareLinkText } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsx("section", {
        "data-component": "authorable/shared/media/share",
        ...editingHydrationProps(isEditing),
        ...getTestProps(`component-share}`),
        children: /*#__PURE__*/ _jsxs("div", {
            className: base(),
            ref: componentRef,
            children: [
                /*#__PURE__*/ _jsx("button", {
                    "aria-label": getDictionaryValue('ShareDialogHeading'),
                    className: cta(),
                    onClick: ()=>handleOnClick(),
                    onKeyDown: (e)=>{
                        if (e.key === 'Escape' && toggleShareMenu) {
                            setToggleShareMenu(false);
                        }
                    },
                    ...getTestProps(`share-button`),
                    children: /*#__PURE__*/ _jsx(SvgIcon, {
                        icon: "share",
                        size: "sm"
                    })
                }),
                toggleShareMenu && /*#__PURE__*/ _jsxs("div", {
                    className: shareListPanel(),
                    onKeyDown: (e)=>{
                        if (e.key === 'Escape') {
                            setToggleShareMenu(false);
                        }
                    },
                    ...getTestProps(`share-panel`),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: header(),
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    children: /*#__PURE__*/ _jsx(Text, {
                                        className: headerText(),
                                        encode: false,
                                        field: {
                                            value: getDictionaryValue('ShareDialogHeading')
                                        },
                                        tag: "p",
                                        ...getTestProps(`dialog-heading`)
                                    })
                                }),
                                /*#__PURE__*/ _jsx("button", {
                                    "aria-label": "Close",
                                    onClick: ()=>setToggleShareMenu(!toggleShareMenu),
                                    ...getTestProps(`close-button`),
                                    children: /*#__PURE__*/ _jsx(SvgIcon, {
                                        className: closeIcon(),
                                        icon: "share-close",
                                        size: "sm"
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: content(),
                            children: socialShareLinks?.map((shareListItem, index)=>{
                                const urlValue = shareListItem?.fields?.url?.value;
                                const isLinkToOpen = urlValue?.includes('http') || urlValue?.toLowerCase()?.includes('mailto');
                                const showCopiedText = isClickedOnCopyLink && shareListItem?.fields?.url?.value?.toLowerCase() === 'copy';
                                return /*#__PURE__*/ _jsx(Fragment, {
                                    children: /*#__PURE__*/ _jsxs("a", {
                                        ...getTestProps(`link-${index}`),
                                        className: shareLinkItem(),
                                        href: `${isLinkToOpen ? `${getRedirectionLink(urlValue)}` : 'javascript:void(0)'} `,
                                        target: `${isLinkToOpen ? '_blank' : ''}`,
                                        onClick: (e)=>{
                                            if (!isLinkToOpen) {
                                                e.preventDefault();
                                                handleShareClick(shareListItem?.fields?.url?.value);
                                            }
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(ImageWrapper, {
                                                field: shareListItem?.fields?.icon,
                                                ...getTestProps(`image-${index}`)
                                            }),
                                            /*#__PURE__*/ _jsx(Text, {
                                                className: shareLinkText(),
                                                encode: false,
                                                field: {
                                                    value: showCopiedText ? getDictionaryValue('ClickedLinkText') : shareListItem?.fields?.title?.value
                                                },
                                                tag: "span",
                                                ...getTestProps(`text-${index}`)
                                            })
                                        ]
                                    })
                                }, index);
                            })
                        })
                    ]
                })
            ]
        })
    });
};
export const Default = withStandardComponentWrapper(Share, false);
const TAILWIND_VARIANTS = tv({
    defaultVariants: {
        style: 'primary'
    },
    slots: {
        base: [
            'print:hidden',
            'relative',
            'z-10'
        ],
        cta: [
            'hover:bg-color-fill-fill-transparent-hover',
            'rounded-sm',
            'p-1'
        ],
        shareListPanel: [
            'absolute',
            'bg-color-surface-surface-hover',
            'flex',
            'flex-col',
            'gap-2',
            'shadow-[0px_8px_16px_-4px]',
            'shadow-color-black-alpha-20',
            'p-4',
            'top-9',
            'min-w-[200px]',
            'rounded-lg',
            'border',
            'border-color-border-border-secondary'
        ],
        content: [
            'flex',
            'flex-col'
        ],
        header: [
            'flex',
            'items-center',
            'justify-between'
        ],
        headerText: [
            'text-color-text-text',
            'text-base',
            'font-bold'
        ],
        closeIcon: [
            'cursor-pointer',
            '!h-6',
            '!w-6'
        ],
        shareLinkItem: [
            'cursor-pointer',
            'flex',
            'flex-row',
            'gap-2',
            'p-2',
            'rounded-sm',
            'hover:bg-color-fill-fill-transparent-hover'
        ],
        shareLinkText: [
            'text-base',
            'font-semibold'
        ]
    }
});
